import { useState, type ComponentType, type KeyboardEvent, type MouseEvent } from 'react';
import { Building2, Folder, User, Users } from 'lucide-react';
import type { OwnerKind } from '@reflex/shared';
import { usePluginAuth, usePluginRegistry } from '@reflex/ui/client/context';
import { useUsers } from '@reflex/ui/client/hooks';
import { cn } from '../../lib/utils';
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '../ui/hover-card';
import { RESOURCE_PREVIEW_HOVER_CONTENT_CLASS } from '../resource-preview/ResourcePreviewCard';
import { ResourcePreview } from '../resource-preview/ResourcePreview';
import { useResolvedOwner } from './useResolvedOwner';

/**
 * Stable icon mapping for the host's built-in ownership kinds. Plugins
 * that contribute their own owner kind (e.g. `project` from
 * plugin-projects) register icon + label metadata through their web
 * manifest's `ownerKinds` field; the chip resolves them via
 * {@link usePluginRegistry} below. Kinds with neither a host built-in
 * nor a plugin registration fall through to {@link FALLBACK_OWNER_ICON}.
 */
export const OWNER_ICON: Record<OwnerKind, ComponentType<{ className?: string }>> = {
  user: User,
  team: Users,
  organization: Building2,
};

export const OWNER_LABEL: Record<OwnerKind, string> = {
  user: 'User',
  team: 'Team',
  organization: 'Organization',
};

/** Generic fallback when no host-built-in or plugin entry matches. */
const FALLBACK_OWNER_ICON: ComponentType<{ className?: string }> = Folder;
const FALLBACK_OWNER_LABEL = 'Owner';

function hostOwnerIcon(kind: string): ComponentType<{ className?: string }> | undefined {
  if (kind === 'user') return OWNER_ICON.user;
  if (kind === 'team') return OWNER_ICON.team;
  if (kind === 'organization') return OWNER_ICON.organization;
  return undefined;
}

function hostOwnerLabel(kind: string): string | undefined {
  if (kind === 'user') return OWNER_LABEL.user;
  if (kind === 'team') return OWNER_LABEL.team;
  if (kind === 'organization') return OWNER_LABEL.organization;
  return undefined;
}

interface OwnerChipProps {
  /**
   * Single prefixed owner id (`usr_*` / `tem_*` / `org_*` / …). Kind is
   * derived from the prefix. `null` is treated as organization-owned
   * for display (legacy host-tier rows).
   */
  ownerId?: string | null;
  /**
   * Optional class applied to the trigger element. Use sparingly — the
   * default sizing keeps every row's owner indicator aligned across the
   * app.
   */
  className?: string;
}

interface PreviewTarget {
  kind: string;
  id: string;
}

/**
 * Compact owner indicator for a list row or card. Renders a 5x5 icon
 * (user / team / organization) and opens the registered resource
 * preview popover on hover; click toggles it for touch devices.
 *
 * Resolves the right preview target internally from the row's `ownerId`:
 *
 * - kind `'user'`         → `{ kind: 'users', id: ownerId }`
 * - kind `'team'`         → `{ kind: 'teams', id: ownerId }`
 * - kind `'organization'` → `{ kind: 'organizations', id: ownerId ?? activeOrganizationId }`
 *
 * Falls back to a `Tooltip` (no preview popover) when no preview target
 * is resolvable.
 */
export function OwnerChip({ ownerId, className }: OwnerChipProps) {
  const { activeOrganizationId } = usePluginAuth();
  const registry = usePluginRegistry();
  // Fire-and-forget org-scoped fetch so the `useResolvedOwner` users read
  // below has data to find. `useUsers` dedupes via React Query, so dozens of
  // chips on the same page only trigger one request.
  useUsers();
  const resolved = useResolvedOwner({ ownerId });
  const displayKind = resolved.ownerKind ?? 'organization';
  const idPrefix =
    resolved.ownerId && resolved.ownerId.includes('_')
      ? (resolved.ownerId.split('_')[0] ?? null)
      : null;
  const pluginEntry = idPrefix ? registry?.ownerKinds?.get(idPrefix) : undefined;
  const Icon = pluginEntry?.icon ?? hostOwnerIcon(displayKind) ?? FALLBACK_OWNER_ICON;
  const ariaLabel = describeOwner({ kind: displayKind, ...resolved }, pluginEntry?.label);
  const target = resolvePreviewTarget(displayKind, {
    ownerId: resolved.ownerId,
    activeOrganizationId,
  });

  const triggerClasses = cn(
    'inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-md text-muted-foreground',
    displayKind === 'user' && 'bg-primary/10 text-primary',
    displayKind === 'team' && 'bg-secondary/40 text-foreground',
    displayKind === 'organization' && 'bg-muted/40 text-muted-foreground',
    className,
  );

  if (!target) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            data-slot="owner-chip"
            data-owner-type={displayKind}
            // `img`: a bare span may not carry `aria-label`
            // (axe `aria-prohibited-attr`); the chip is an icon-only glyph.
            role="img"
            aria-label={ariaLabel}
            className={triggerClasses}
          >
            <Icon className="h-3 w-3" />
          </span>
        </TooltipTrigger>
        <TooltipContent>{ariaLabel}</TooltipContent>
      </Tooltip>
    );
  }

  return (
    <OwnerChipPreview
      kind={displayKind}
      ownerName={resolved.ownerName}
      target={target}
      ariaLabel={ariaLabel}
      icon={Icon}
      triggerClasses={triggerClasses}
    />
  );
}

interface OwnerChipPreviewProps {
  kind: string;
  ownerName: string | null;
  target: PreviewTarget;
  ariaLabel: string;
  icon: ComponentType<{ className?: string }>;
  triggerClasses: string;
}

/**
 * HoverCard variant of `OwnerChip` rendered when we have a preview
 * target. Hover opens via Radix's built-in delay; click (and
 * Enter/Space) toggle controlled state so the chip works on touch.
 */
function OwnerChipPreview({
  kind,
  ownerName,
  target,
  ariaLabel,
  icon: Icon,
  triggerClasses,
}: OwnerChipPreviewProps) {
  const [open, setOpen] = useState(false);

  function handleClick(event: MouseEvent<HTMLSpanElement>) {
    event.preventDefault();
    event.stopPropagation();
    setOpen((prev) => !prev);
  }

  function handleKeyDown(event: KeyboardEvent<HTMLSpanElement>) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      event.stopPropagation();
      setOpen((prev) => !prev);
    }
  }

  return (
    <HoverCard open={open} onOpenChange={setOpen} openDelay={150} closeDelay={100}>
      <HoverCardTrigger asChild>
        <span
          data-slot="owner-chip"
          data-owner-type={kind}
          data-state={open ? 'open' : 'closed'}
          role="button"
          tabIndex={0}
          aria-label={ariaLabel}
          aria-expanded={open}
          aria-haspopup="dialog"
          onClick={handleClick}
          onKeyDown={handleKeyDown}
          className={cn(
            triggerClasses,
            'cursor-pointer transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          )}
        >
          <Icon className="h-3 w-3" />
        </span>
      </HoverCardTrigger>
      <HoverCardContent
        align="start"
        className={RESOURCE_PREVIEW_HOVER_CONTENT_CLASS}
        aria-label={ariaLabel}
      >
        <ResourcePreview kind={target.kind} id={target.id} fallbackName={ownerName ?? undefined} />
      </HoverCardContent>
    </HoverCard>
  );
}

function resolvePreviewTarget(
  kind: string,
  ctx: {
    ownerId: string | null;
    activeOrganizationId: string | null;
  },
): PreviewTarget | null {
  if (kind === 'user' && ctx.ownerId) {
    return { kind: 'users', id: ctx.ownerId };
  }
  if (kind === 'team' && ctx.ownerId) {
    return { kind: 'teams', id: ctx.ownerId };
  }
  if (kind === 'organization') {
    const orgId = ctx.ownerId ?? ctx.activeOrganizationId;
    if (orgId) return { kind: 'organizations', id: orgId };
  }
  return null;
}

interface ResolvedOwnerLike {
  kind: string;
  ownerName: string | null;
  isCurrentUser: boolean;
}

function describeOwner(resolved: ResolvedOwnerLike, pluginLabel?: string): string {
  if (resolved.kind === 'organization') return OWNER_LABEL.organization;
  if (resolved.kind === 'team') {
    return resolved.ownerName ? `${resolved.ownerName} team` : 'Team';
  }
  if (resolved.kind === 'user') {
    if (resolved.isCurrentUser) {
      return resolved.ownerName ? `${resolved.ownerName} (you)` : 'Mine';
    }
    return resolved.ownerName ?? OWNER_LABEL.user;
  }
  // Plugin-contributed owner kind (e.g. `project` from plugin-projects).
  // Prefer the resolved name when present; otherwise fall back to the
  // contributor-supplied label, then the generic owner label.
  return resolved.ownerName ?? pluginLabel ?? hostOwnerLabel(resolved.kind) ?? FALLBACK_OWNER_LABEL;
}
